import { Box, Flex, Stack } from '@chakra-ui/react'
import { DateTime } from 'luxon'

import { millisecondsToHumanReadableString } from '@/utils/time'

import {
  requiresChangesBeforeReview,
  VOLUNTEER_ACTIVITY_TO_LABEL,
} from '@/client/services/volunteerActivity'

import ApproveButton from '@/components/Admin/VolunteerHours/ApproveButton'
import RejectButton from '@/components/Admin/VolunteerHours/RejectButton'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import { Links } from '../Links'
import type { VolunteerActivityComponentAttributes } from '../types'
import ActivityModalItem from './ActivityModalItem'
import { ChangesRequestedBanner } from './ChangesRequestedBanner'

export default function ActivityModal({
  open,
  activity,
  onClose,
}: {
  open: boolean
  activity: VolunteerActivityComponentAttributes
  onClose: () => void
}): JSX.Element {
  const {
    id,
    type,
    description,
    date,
    duration_seconds: durationSeconds,
    links,
    created_at: createdAt,
    requested_changes: requestedChanges,
    requested_changes_at: requestedChangesAt,
    last_submitted_at: lastSubmittedAt,
    reviewer,
  } = activity

  const updatedAt = DateTime.fromJSDate(lastSubmittedAt).toLocaleString(DateTime.DATE_SHORT)
  const dateTime = DateTime.fromJSDate(date)
  const dateTimeText = dateTime.toLocaleString(DateTime.DATE_SHORT)
  const requiresChanges = requiresChangesBeforeReview({ requestedChangesAt, lastSubmittedAt })
  const durationText = millisecondsToHumanReadableString(durationSeconds * 1000, {
    maxUnit: 'hour',
  })
  const createdAtText = DateTime.fromJSDate(createdAt).toLocaleString(DateTime.DATE_SHORT)

  return (
    <ModalV3
      size="xl"
      open={open}
      title={VOLUNTEER_ACTIVITY_TO_LABEL[type]}
      body={() => (
        <Stack direction="column" spacing={4}>
          <ChangesRequestedBanner
            reviewer={reviewer}
            requestedChanges={requestedChanges}
            requestedChangesAt={requestedChangesAt}
            lastSubmittedAt={lastSubmittedAt}
          />
          <Flex justify="space-between">
            <ActivityModalItem title="Date" description={dateTimeText} />
            <ActivityModalItem title="Duration" description={durationText} />
          </Flex>
          <ActivityModalItem title="Description" description={description} />
          <ActivityModalItem
            title="Relevant Links"
            description={() => (
              <Box mt={0}>
                <Links links={links} />
              </Box>
            )}
          />
          <ActivityModalItem title="Submitted At" description={createdAtText} />
          <ActivityModalItem title="Updated At" description={updatedAt} />
          <Flex justify="flex-end">
            <Stack direction="row" spacing={2}>
              <RejectButton
                id={id}
                requiresChangesBeforeReview={requiresChanges}
                onReject={onClose}
              />
              <ApproveButton
                id={id}
                onApprove={onClose}
                requiresChangesBeforeReview={requiresChanges}
              />
            </Stack>
          </Flex>
        </Stack>
      )}
      onClose={onClose}
    />
  )
}
